import {
	ReactElement,
	useState,
	useEffect,
	useMemo,
	useRef,
	Ref
} from "react";
import {
	TableView,
	ConfirmationDialog,
	FormattedText,
	DateString,
	Pagination,
	ContextMenu,
	SearchInputForm,
	computeContextMenuTitle,
	useContextMenu,
	SearchPlaceholder,
	ToolbarAction,
	FloatingActionButton,
	ToolbarRegularAction,
	TableViewNoResultConfig
} from "client/Components";
import { localize } from "client_legacy/ndaw/culture";
import {
	useSelection,
	useSortHandler,
	useDeepComparison,
	AlertManager,
	useAlerts,
	createLocalDataSource,
	useUserTimezone,
	useWorkspaceMediaQuery,
	MediaQueryKind
} from "client/Hooks";
import {
	ApiKeyGridItem,
	ApiKeyScopeKind,
	list,
	regenerate,
	deleteMultiple
} from "client/Api/ApiKeys/apiKeys";
import { ConfigureApiKeyDialog } from "./ConfigureApiKeyDialog";
import { RevealApiKeyDialog } from "./RevealApiKeyDialog";
import { DataSourceSortableState, DataSourceSortDirection } from "client/Data/sorting";
import { contains, normalizeForSearch } from "client/Data/searching";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { SettingsPage } from "../SettingsPage";
import {
	ActionKind,
	buildActionsForTopBar,
	deselectAllTitle
} from "client_legacy/ndaw/actions";
import { handlePageDataLoadingError } from "client/Features/ErrorPage/errorStore";
import { useActiveSettingsMenuItem } from "../SidebarMenu/sidebarMenuSelectionStore";
import { SettingsPageKind } from "client/Api/SettingsLayout/settingsLayout";
import { TimeZone } from "client/DateAndTime/timezones";
import { AiAssistantFabSpacing } from "client/Features/ChatAssistant/AiAssistantFabSpacing";

type ConfigureApiKeyDialogState = {
	isVisible: boolean;
	existingItem?: ApiKeyGridItem;
};

export const scopeToDisplayName: { [key in ApiKeyScopeKind]: string; } = {
	[ApiKeyScopeKind.Activity]: localize("_ActivityFeedApi"),
	[ApiKeyScopeKind.IntegrationCredentials]: localize("_IntegrationCredentialsApi"),
	[ApiKeyScopeKind.UserApps]: localize("_UserAppsApi"),
	[ApiKeyScopeKind.Announcements]: localize("_AnnouncementsApi"),
	[ApiKeyScopeKind.AppIcons]: localize("_AppIconsApi"),
	[ApiKeyScopeKind.Events]: localize("_EventsApi")
};

const scopeToNormalizedName: { [key in ApiKeyScopeKind]: string } = {
	[ApiKeyScopeKind.Activity]: normalizeForSearch(
		localize("_ActivityFeedApi")
	),
	[ApiKeyScopeKind.IntegrationCredentials]: normalizeForSearch(
		localize("_IntegrationCredentialsApi")
	),
	[ApiKeyScopeKind.UserApps]: normalizeForSearch(localize("_UserAppsApi")),
	[ApiKeyScopeKind.Announcements]: normalizeForSearch(
		localize("_AnnouncementsApi")
	),
	[ApiKeyScopeKind.AppIcons]: normalizeForSearch(localize("_AppIconsApi")),
	[ApiKeyScopeKind.Events]: normalizeForSearch(localize("_EventsApi"))
};

const useApiKeysDataSource = createLocalDataSource<ApiKeyGridItem>()
	.setConfiguration({
		search: {
			doesMatchItem: searchMatchFunction,
		},
		hasPagination: true
	})
	.getHook();

export const ApiKeysOverview = (): ReactElement => {
	const [configureDialogState, setConfigureDialogState] = useState<ConfigureApiKeyDialogState>({
		isVisible: false
	});

	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [allItems, setAllItems] = useState<ReadonlyArray<ApiKeyGridItem>>([]);
	const [itemForRegenerationConfirmation, setItemForRegenerationConfirmation] = useState<ApiKeyGridItem | null>(null);
	const [itemsForDeletionConfirmation, setItemsForDeletionConfirmation] = useState<ReadonlyArray<ApiKeyGridItem>>([]);
	const [keyToReveal, setKeyToReveal] = useState<string | null>(null);
	const tableRef: Ref<HTMLDivElement> = useRef<HTMLDivElement>(null);
	const [contextMenuHandler, contextMenuProps] = useContextMenu(tableRef);
	const alertManager: AlertManager = useAlerts();
	const userTimezone: TimeZone = useUserTimezone();
	const isResponsive: boolean = useWorkspaceMediaQuery(MediaQueryKind.Responsive);
	const isMobile: boolean = useWorkspaceMediaQuery(MediaQueryKind.Mobile);
	useActiveSettingsMenuItem(SettingsPageKind.ApiKeys);

	useEffect(() => {
		async function fetchItems(signal: AbortSignal): Promise<void> {
			setIsLoading(true);

			try {
				const items: ApiKeyGridItem[] = await list(signal)
					.catch(handlePageDataLoadingError);

				setAllItems(items);
				setIsLoading(false);
			} catch(e) {
				if (isCancelledError(e)) {
					return;
				}

				console.error(e);
				alertManager.showError(localize("_GeneralErrorMessage"));
				setIsLoading(false);
			}
		}

		const cancellation = new AbortController();
		fetchItems(cancellation.signal);

		return () => cancellation.abort();
	}, [alertManager]);

	const [sortingState, setSortingState] = useState<DataSourceSortableState<ApiKeyGridItem>>(
		{ propertyName: "name", direction: DataSourceSortDirection.Ascending }
	);
	const [currentPageNumber, setCurrentPageNumber] = useState<number>(0);
	const [searchTerm, setSearchTerm] = useState<string>("");

	const { sortingFor } = useSortHandler<ApiKeyGridItem>(sortingState, setSortingState);
	const itemsModifiersVersion: number = useDeepComparison([sortingState, searchTerm]);

	useEffect(
		() => setCurrentPageNumber(0),
		[itemsModifiersVersion]
	);

	const { currentItems, pagination } = useApiKeysDataSource(
		allItems, {
		sorting: sortingState,
		pagination: {
			currentPageNumber,
			amountPerPage: 25
		},
		searchTerm
	});

	const [selectedItems, setSelectedItems] = useState<ReadonlyArray<ApiKeyGridItem>>([]);

	const { selectionFor, multiSelection } = useSelection(
		currentItems,
		"id",
		{
			selectedItems,
			setSelectedItems,
			isMultiSelect: true,
			hasMemorization: false
		}
	);

	const actionContext = useMemo(
		(): ApiKeyActionContext => ({
			selection: selectedItems,
			isMobile
		}),
		[isMobile, selectedItems]
	);

	const selectionActions = useMemo(
		(): readonly ApiKeyAction[] => {
			return getSelectionActions({
				deselectHandler: () => setSelectedItems([])
			});
		},
		[]
	);

	const contextualActions = useMemo(
		(): readonly ApiKeyAction[] => {
			return listContextualActions(
				(key) => setItemForRegenerationConfirmation(key),
				(key) => setConfigureDialogState({ isVisible: true, existingItem: key }),
				(keys) => setItemsForDeletionConfirmation(keys)
			);
		},
		[]
	);

	const mainAction = useMemo(
		(): ToolbarRegularAction<ApiKeyActionContext> => {
			return getMainAction(
				() => setConfigureDialogState({isVisible: true})
			);
		},
		[]
	);

	const hasSelection: boolean = actionContext.selection
		.length !== 0;

	const topBarActions = useMemo(
		(): readonly ApiKeyAction[] => {
			return buildActionsForTopBar<ApiKeyActionContext, ApiKeyAction>(
				{
					mainActions: [mainAction],
					selectionActions,
					contextualActions
				},
				hasSelection,
				isResponsive,
				false
			);
		},
		[
			hasSelection,
			contextualActions,
			isResponsive,
			mainAction,
			selectionActions
		]
	);

	const addOrUpdateItem = (item: ApiKeyGridItem): void => {
		setAllItems(allItems => {
			const existingItemIndex: number = allItems.findIndex(i => i.id === item.id);
			const copy: ApiKeyGridItem[] = [...allItems];

			if (existingItemIndex === -1) {
				copy.push(item);
			} else {
				copy.splice(existingItemIndex, 1, item);
			}

			return copy;
		});
	};

	const confirmRegenerationHandler = async (): Promise<void> => {
		const item: ApiKeyGridItem | null = itemForRegenerationConfirmation;
		setItemForRegenerationConfirmation(null);

		if (!item) {
			throw new Error("No item has been selected for regeneration.");
		}

		try {
			const { key, generatedOn } = await regenerate(item.id);

			addOrUpdateItem({
				...item,
				generatedOn
			});

			setKeyToReveal(key);
		} catch (e) {
			console.error(e);
			alertManager.showError(localize("_GeneralErrorMessage"));
		} finally {
			setIsLoading(false);
		}
	};

	const confirmDeletionHandler = async (): Promise<void> => {
		const items: ReadonlyArray<ApiKeyGridItem> = itemsForDeletionConfirmation;
		setItemsForDeletionConfirmation([]);
		setIsLoading(true);

		if (items.length === 0) {
			throw new Error("No items have been selected for regeneration.");
		}

		try {
			await deleteMultiple(items.map(i => i.id));
			setAllItems(allItems => {
				return allItems.filter(i => !items.some(di => i.id === di.id));
			});
		} catch (e) {
			console.error(e);
			alertManager.showError(localize("_GeneralErrorMessage"));
		} finally {
			setIsLoading(false);
		}
	};

	const noResultConfig = useMemo(
		(): TableViewNoResultConfig => {
			if (searchTerm) {
				return {
					icon: "magnifying-glass",
					title: localize("_NoSearchResults"),
					message: localize("_PleaseTrySearchingWithAnotherTerm")
				};
			}

			return {
				icon: "gear",
				title: localize("_NoApiKeysYet"),
				message: localize("_ThereAreNoApiKeysYet")
			};
		},
		[searchTerm]
	);

	return (
		<>
			<SettingsPage
				hasBusyIndicator={isLoading}
				actions={topBarActions}
				actionContext={actionContext}
				hasSelection={hasSelection}
				title={localize("_ApiKeys")}
			>
				<SettingsPage.Section isVisible={allItems.length === 0 && !isLoading}>
					<p>{localize("_CurrentlyYouDontHaveAnyApiKeysConfigured")}</p>
				</SettingsPage.Section>
				<SettingsPage.Section
					height="stretch"
				>
					<SettingsPage.SearchSection>
						<SearchInputForm
							onChange={setSearchTerm}
							trackingId="api-keys-search-form"
							icon="filter"
							height="large"
							placeholder={(
								<SearchPlaceholder
									kind="filter"
									properties={[
										localize("_Name"),
										localize("_Scope")
									]}
								/>
							)}
						/>
					</SettingsPage.SearchSection>
					<SettingsPage.Section height="stretch">
						<TableView
							multiSelectable={multiSelection}
							items={currentItems}
							itemKey="id"
							height="stretch"
							columns={[
								{
									title: localize("_Name"),
									sortable: sortingFor("name"),
									width: "fill",
									minWidth: 200,
									trackingId: "name"
								},
								{
									title: localize("_Scope"),
									trackingId: "scope"
								},
								{
									title: localize("_Status"),
									sortable: sortingFor("isEnabled"),
									trackingId: "status"
								},
								{
									title: localize("_Generated"),
									sortable: sortingFor("generatedOn"),
									trackingId: "generated-on"
								}
							]}
							trackingId="api-keys-table"
							ref={tableRef}
							noResultConfig={noResultConfig}
						>
							{item => (
								<TableView.Row
									selectable={selectionFor(item)}
									key={item.id}
									onExecute={() => setConfigureDialogState({ isVisible: true, existingItem: item })}
									onContextMenu={contextMenuHandler}
									trackingId={item.id.toString()}
								>
									<TableView.Cell>{item.name}</TableView.Cell>
									<TableView.Cell>{formatScopeCollection(item.scope)}</TableView.Cell>
									<TableView.Cell>{localize(item.isEnabled ? "_Enabled" : "_Disabled")}</TableView.Cell>
									<TableView.Cell>
										{item.generatedOn
											? (
												<DateString
													value={item.generatedOn}
													format="AbsoluteTimestamp"
													timezone={userTimezone}
												/>
											)
											: localize("_Unknown")
										}
									</TableView.Cell>
								</TableView.Row>
							)}
						</TableView>
						<Pagination {...pagination} onPageNumberChanged={setCurrentPageNumber} />
						{contextMenuProps && (
							<ContextMenu<ApiKeyActionContext>
								{... contextMenuProps}
								actions={contextualActions}
								context={actionContext}
								trackingId="api-keys-overview-context-menu"
								title={computeContextMenuTitle(
									actionContext.selection,
									(x) => x.name
								)}
							/>
						)}
					</SettingsPage.Section>
				</SettingsPage.Section>
				{mainAction.canExecute(actionContext) && (
					<>
						<div className="preserve-space-for-mobile-plus-button"/>
						<FloatingActionButton
							onClick={() => mainAction.execute(actionContext)}
							icon="plus"
							trackingId="new-button"
						/>
					</>
				)}
				<AiAssistantFabSpacing />
			</SettingsPage>
			{configureDialogState.isVisible && (
				<ConfigureApiKeyDialog
					existingItemId={configureDialogState.existingItem?.id}
					onClose={() => setConfigureDialogState({isVisible: false})}
					onAfterSave={addOrUpdateItem}
					onRevealApiKey={setKeyToReveal}
				/>
			)}
			{itemForRegenerationConfirmation && (
				<ConfirmationDialog
					title={localize("_AreYouSureYouWantToRegenerateTheApiKey")}
					confirmText={localize("_RegenerateApiKey")}
					onConfirmation={confirmRegenerationHandler}
					onCancellation={() => setItemForRegenerationConfirmation(null)}
					trackingId="regenerate-api-key-confirmation-dialog"
				>
					<FormattedText
						text={localize("_NoteTheCurrentApiKeyWillBeRevoked")}
						thingsToReplace={[
							{ what: "{{Note}}", filler: <strong>{localize("_Note")}</strong> }
						]}
					/>
				</ConfirmationDialog>
			)}
			{itemsForDeletionConfirmation.length > 0 && (
				<ConfirmationDialog
					title={
						itemsForDeletionConfirmation.length === 1
							? localize("_AreYouSureYouWantDoDeleteThisApiKey")
							: localize("_AreYouSureYouWantToDeleteTheseApiKeys")
					}
					confirmText={localize("_Delete")}
					onConfirmation={confirmDeletionHandler}
					onCancellation={() => setItemsForDeletionConfirmation([])}
					trackingId="delete-api-key-confirmation-dialog"
				/>
			)}
			{keyToReveal &&
				<RevealApiKeyDialog apiKey={keyToReveal} onClose={() => setKeyToReveal(null)} />
			}
		</>
	);
};
ApiKeysOverview.displayName = "ApiKeysOverview";

function searchMatchFunction(
	item: ApiKeyGridItem,
	normalizedNotEmptySearchTerm: Exclude<string, "">
): boolean {
	const {
		name,
		scope
	} = item;

	if (contains(name, normalizedNotEmptySearchTerm)) {
		return true;
	}

	if (scope) {
		for (const individualScope of scope) {
			const scopeName: string | undefined = scopeToNormalizedName[individualScope];
			if (contains(scopeName, normalizedNotEmptySearchTerm)) {
				return true;
			}
		}
	}

	return false;
}

function formatScopeCollection(scope: ReadonlyArray<ApiKeyScopeKind>): string {
	return scope.map(s => scopeToDisplayName[s]).join(", ");
}

type ApiKeyActionContext = {
	selection: readonly ApiKeyGridItem[];
	isMobile: boolean;
};

type ApiKeyAction = ToolbarAction<ApiKeyActionContext>;

function listContextualActions(
	regenerateHandler: (item: ApiKeyGridItem) => void,
	editHandler: (item: ApiKeyGridItem) => void,
	deleteHandler: (items: readonly ApiKeyGridItem[]) => void
): readonly ApiKeyAction[] {
	return [
		{
			kind: ActionKind.Regular,
			name: "regenerate",
			title: localize("_RegenerateApiKey"),
			canExecute: (c) => c.selection.length === 1,
			execute: (c) => regenerateHandler(c.selection[0]),
			icon: "arrow-rotate-right"
		},
		{
			kind: ActionKind.Regular,
			name: "edit",
			title: localize("_Edit"),
			canExecute: (c) => c.selection.length === 1,
			execute: (c) => editHandler(c.selection[0]),
			icon: "pen"
		},
		{
			kind: ActionKind.Regular,
			name: "delete",
			title: localize("_Delete"),
			canExecute: (c) => c.selection.length > 0,
			execute: (c) => deleteHandler(c.selection),
			icon: "trash-can"
		}
	];
}

type SelectionActionsHandlers = {
	deselectHandler: () => void;
};

function getSelectionActions(handlers: SelectionActionsHandlers): readonly ApiKeyAction[] {
	return [
		{
			kind: ActionKind.Regular,
			name: "deselectAll",
			title: (c) => deselectAllTitle(c.selection),
			icon: "circle-xmark",
			canExecute: (c) => c.selection.length > 0,
			execute: () => handlers.deselectHandler()
		},
		{
			kind: ActionKind.Divider,
			name: "deselect-all-divider",
			canExecute: (c) => !c.isMobile
				&& c.selection.length > 0
		}
	];
}

function getMainAction(
	newHandler: () => void
): ToolbarRegularAction<ApiKeyActionContext> {
	return {
		kind: ActionKind.Regular,
		name: "createNew",
		title: localize("_New"),
		canExecute: () => true,
		execute: () => newHandler(),
		icon: "plus",
		isPrimary: true
	};
}

export const apiKeysRouting = {
	overview: "/ApiKeys/Page"
};